<template>
  <div>
    <h2>Ingresa tu número de teléfono</h2>

    <!-- Selector de país personalizado con banderas -->
    <div class="custom-select-container">
      <div 
        class="selected-option" 
        @click="toggleDropdown"
      >
        <div v-if="selectedCountry" class="country-item">
          <img 
            v-if="getSelectedCountry && getSelectedCountry.flag" 
            :src="getSelectedCountry.flag" 
            :alt="`Bandera de ${getSelectedCountry.name}`" 
            class="flag-img"
          />
          <span>{{ getSelectedCountry ? getSelectedCountry.name : '' }} ({{ selectedCountry }})</span>
        </div>
        <div v-else class="placeholder">Selecciona un país</div>
        <div class="arrow-down"></div>
      </div>

      <!-- Dropdown options -->
      <div class="dropdown-menu" v-if="isOpen">
        <div 
          v-for="country in filteredCountries" 
          :key="country.dial_code"
          class="country-option"
          @click="selectCountry(country.dial_code)"
        >
          <img 
            v-if="country.flag" 
            :src="country.flag" 
            :alt="`Bandera de ${country.name}`" 
            class="flag-img"
          />
          <span>{{ country.name }} ({{ country.dial_code }})</span>
        </div>
      </div>
    </div>

    <!-- Input para el número de teléfono -->
    <div class="phone-input">
      <input v-model="phone" type="text" placeholder="Número de teléfono" />
    </div>
    
    <button @click="sendOTP" :disabled="!phone || !selectedCountry">Enviar Código</button>
    
    <p v-if="error" class="error-message">{{ error }}</p>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted } from "vue";
import { useStore } from "vuex";
import axios from "axios";
import { useRouter } from "vue-router";

const router = useRouter();
const store = useStore();
const session = computed(() => store.getters.getUserSession);

const phone = ref("");
const error = ref("");
const countryCodes = ref([]);
const selectedCountry = ref("");
const isOpen = ref(false);


// Computed property para obtener el país seleccionado completo
const getSelectedCountry = computed(() => {
  return countryCodes.value.find(country => country.dial_code === selectedCountry.value);
});

// Función para cerrar el dropdown cuando se hace clic fuera
const closeDropdown = (e) => {
  const container = document.querySelector('.custom-select-container');
  if (container && !container.contains(e.target)) {
    isOpen.value = false;
  }
};

// Event listener para cerrar el dropdown al hacer clic fuera
onMounted(() => {
  document.addEventListener('click', closeDropdown);
  loadCountryCodes();
});

onUnmounted(() => {
  document.removeEventListener('click', closeDropdown);
});

// Toggle dropdown
const toggleDropdown = (e) => {
  e.stopPropagation();
  isOpen.value = !isOpen.value;
};

// Select country
const selectCountry = (dialCode) => {
  selectedCountry.value = dialCode;
  isOpen.value = false;
};

const filteredCountries = computed(() => {
  return countryCodes.value;
});

// Modificar loadCountryCodes para usar el nuevo endpoint que devuelve solo países activos
const loadCountryCodes = async () => {
  const token = session.value ? session.value.token_api : null;

  if (!token) {
    error.value = "No se encontró la sesión del usuario. Vuelve a iniciar sesión.";
    return;
  }

  // Usamos el nuevo endpoint que devuelve solo los países activos
  const url = `${process.env.VUE_APP_API_BASE_URL}/get-country-preferences`;
  const options = {
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token.trim()}`,
    },
  };

  try {
    const response = await axios.get(url, options);
    // Ahora la API nos devuelve directamente los países activos
    countryCodes.value = response.data;
    console.log("Códigos de país activos cargados:", countryCodes.value);

    // Establecer un valor predeterminado (opcional)
    if (countryCodes.value.length > 0) {
      const defaultCountry = countryCodes.value.find(c => 
        c.name === "Mexico" || c.name === "Spain"
      ) || countryCodes.value[0];
      
      selectedCountry.value = defaultCountry.dial_code;
    }
  } catch (err) {
    console.error("Error al cargar códigos de país:", err);
    error.value = "No se pudieron cargar los códigos de país.";
  }
};

// Enviar OTP con código de país seleccionado
const sendOTP = async () => {
  const token = session.value ? session.value.token_api : null;

  if (!token) {
    error.value = "No se encontró la sesión del usuario. Vuelve a iniciar sesión.";
    return;
  }

  const url = `${process.env.VUE_APP_API_BASE_URL}/send-otp`;

  const options = {
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token.trim()}`,
    },
  };

  try {
    const response = await axios.post(
      url,
      {
        user_id: 3,
        phone_number: selectedCountry.value + phone.value, // Concatenar código de país con el número
      },
      options
    );

    if (response.status === 200) {
      router.push({ name: "VerifyOTP", query: { phone: phone.value } });
    } else if (response.status === 401) {
      error.value = "No autorizado. Vuelve a iniciar sesión.";
    }
  } catch (err) {
    console.error("Error al enviar código:", err);
    error.value = err.response?.data?.detail || "Error al enviar el código de verificación.";
  }
};
</script>

<style scoped>
.custom-select-container {
  position: relative;
  width: 100%;
  max-width: 300px;
  margin-bottom: 15px;
  user-select: none;
}

.selected-option {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 8px 12px;
  border: 1px solid #ccc;
  border-radius: 4px;
  background-color: white;
  cursor: pointer;
}

.country-item, .country-option {
  display: flex;
  align-items: center;
  gap: 8px;
}

.placeholder {
  color: #666;
}

.arrow-down {
  width: 0;
  height: 0;
  border-left: 5px solid transparent;
  border-right: 5px solid transparent;
  border-top: 5px solid #666;
  margin-left: 8px;
}

.dropdown-menu {
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  max-height: 300px;
  overflow-y: auto;
  background-color: white;
  border: 1px solid #ccc;
  border-top: none;
  border-radius: 0 0 4px 4px;
  z-index: 100;
}

.country-option {
  padding: 8px 12px;
  cursor: pointer;
}

.country-option:hover {
  background-color: #f5f5f5;
}

.flag-img {
  width: 24px;
  height: auto;
  vertical-align: middle;
  border: 1px solid #ddd;
}

.phone-input {
  margin-bottom: 15px;
}

.error-message {
  color: red;
}

input {
  padding: 8px;
  width: 100%;
  max-width: 300px;
}

button {
  padding: 8px 16px;
  background-color: #4CAF50;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
}

button:disabled {
  background-color: #cccccc;
  cursor: not-allowed;
}
</style>